import { call, put, takeEvery, takeLatest, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { statusApi, Status, StatusSearchParams } from '../../services/statusApi';
import {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setValidationErrors,
  setPagination,
  setStatistics,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  FETCH_STATUSES,
  FETCH_STATUS_BY_ID,
  CREATE_STATUS,
  UPDATE_STATUS,
  DELETE_STATUS,
  SEARCH_STATUSES,
  FETCH_STATISTICS,
  BULK_ACTIVATE,
  BULK_DEACTIVATE,
  BULK_DELETE,
  EXPORT_TO_CSV,
} from '../reducers/statusSlice';

// Worker Sagas
function* fetchStatusesSaga(action: PayloadAction<StatusSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const params = action.payload || {};
    const response = yield call(statusApi.getAll, params);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error fetching Statuses:', error);
    yield put(setError(error.message || 'Failed to fetch Statuses'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchStatusByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const status = yield call(statusApi.getById, action.payload);
    yield put(setSelectedItem(status));
  } catch (error: any) {
    console.error('Error fetching Status:', error);
    yield put(setError(error.message || 'Failed to fetch Status'));
  } finally {
    yield put(setLoading(false));
  }
}

function* createStatusSaga(action: PayloadAction<Omit<Status, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(statusApi.validation.validateStatus, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newStatus = yield call(statusApi.create, action.payload);
    yield put(addItem(newStatus));
    yield put(setSuccessMessage('Status created successfully'));
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      // Note: In a real saga, you'd use a different approach for this
      // This is simplified for demonstration
    }, 3000);
  } catch (error: any) {
    console.error('Error creating Status:', error);
    yield put(setError(error.message || 'Failed to create Status'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateStatusSaga(action: PayloadAction<{ id: number; status: Partial<Status> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const { id, status } = action.payload;
    
    // Validate the request
    const validationErrors = yield call(statusApi.validation.validateStatus, status);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedStatus = yield call(statusApi.update, id, status);
    yield put(updateItem(updatedStatus));
    yield put(setSuccessMessage('Status updated successfully'));
  } catch (error: any) {
    console.error('Error updating Status:', error);
    yield put(setError(error.message || 'Failed to update Status'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deleteStatusSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(statusApi.delete, action.payload);
    if (response.success) {
      yield put(removeItem(action.payload));
      yield put(setSuccessMessage('Status deleted successfully'));
    } else {
      yield put(setError(response.message || 'Failed to delete Status'));
    }
  } catch (error: any) {
    console.error('Error deleting Status:', error);
    yield put(setError(error.message || 'Failed to delete Status'));
  } finally {
    yield put(setLoading(false));
  }
}

function* searchStatusesSaga(action: PayloadAction<StatusSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(statusApi.search, action.payload);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: action.payload.page || 1,
      pageSize: action.payload.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error searching Statuses:', error);
    yield put(setError(error.message || 'Failed to search Statuses'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(statusApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching statistics:', error);
    // Don't set error for statistics as it's not critical
  }
}

function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(statusApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(statusApi.bulkActivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully activated ${action.payload.length} Statuses`));
      // Refresh the list to show updated status
      yield call(fetchStatusesSaga, { payload: undefined, type: FETCH_STATUSES });
    } else {
      yield put(setError(response.message || 'Failed to activate Statuses'));
    }
  } catch (error: any) {
    console.error('Error bulk activating Statuses:', error);
    yield put(setError(error.message || 'Failed to activate Statuses'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(statusApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(statusApi.bulkDeactivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully deactivated ${action.payload.length} Statuses`));
      // Refresh the list to show updated status
      yield call(fetchStatusesSaga, { payload: undefined, type: FETCH_STATUSES });
    } else {
      yield put(setError(response.message || 'Failed to deactivate Statuses'));
    }
  } catch (error: any) {
    console.error('Error bulk deactivating Statuses:', error);
    yield put(setError(error.message || 'Failed to deactivate Statuses'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(statusApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(statusApi.bulkDelete, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully deleted ${action.payload.length} Statuses`));
      // Refresh the list to show updated items
      yield call(fetchStatusesSaga, { payload: undefined, type: FETCH_STATUSES });
    } else {
      yield put(setError(response.message || 'Failed to delete Statuses'));
    }
  } catch (error: any) {
    console.error('Error bulk deleting Statuses:', error);
    yield put(setError(error.message || 'Failed to delete Statuses'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* exportToCSVSaga(action: PayloadAction<StatusSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const params = action.payload || {};
    const blob = yield call(statusApi.exportToCSV, params);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `statuses-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    yield put(setSuccessMessage('Statuses exported successfully'));
  } catch (error: any) {
    console.error('Error exporting Statuses:', error);
    yield put(setError(error.message || 'Failed to export Statuses'));
  } finally {
    yield put(setLoading(false));
  }
}

// Watcher Sagas
function* watchFetchStatuses(): Generator<any, void, any> {
  yield takeLatest(FETCH_STATUSES, fetchStatusesSaga);
}

function* watchFetchStatusById(): Generator<any, void, any> {
  yield takeEvery(FETCH_STATUS_BY_ID, fetchStatusByIdSaga);
}

function* watchCreateStatus(): Generator<any, void, any> {
  yield takeEvery(CREATE_STATUS, createStatusSaga);
}

function* watchUpdateStatus(): Generator<any, void, any> {
  yield takeEvery(UPDATE_STATUS, updateStatusSaga);
}

function* watchDeleteStatus(): Generator<any, void, any> {
  yield takeEvery(DELETE_STATUS, deleteStatusSaga);
}

function* watchSearchStatuses(): Generator<any, void, any> {
  yield takeLatest(SEARCH_STATUSES, searchStatusesSaga);
}

function* watchFetchStatistics(): Generator<any, void, any> {
  yield takeLatest(FETCH_STATISTICS, fetchStatisticsSaga);
}

function* watchBulkActivate(): Generator<any, void, any> {
  yield takeEvery(BULK_ACTIVATE, bulkActivateSaga);
}

function* watchBulkDeactivate(): Generator<any, void, any> {
  yield takeEvery(BULK_DEACTIVATE, bulkDeactivateSaga);
}

function* watchBulkDelete(): Generator<any, void, any> {
  yield takeEvery(BULK_DELETE, bulkDeleteSaga);
}

function* watchExportToCSV(): Generator<any, void, any> {
  yield takeEvery(EXPORT_TO_CSV, exportToCSVSaga);
}

// Root saga
export function* statusSaga(): Generator<any, void, any> {
  yield all([
    watchFetchStatuses(),
    watchFetchStatusById(),
    watchCreateStatus(),
    watchUpdateStatus(),
    watchDeleteStatus(),
    watchSearchStatuses(),
    watchFetchStatistics(),
    watchBulkActivate(),
    watchBulkDeactivate(),
    watchBulkDelete(),
    watchExportToCSV(),
  ]);
} 